import type { ReactNode } from 'react'
import { ESTIMATE_TUNING } from '@/lib/coaching/estimate-minutes'
import { formatMinutesDuration } from '../_lib/duration'

export const ESTIMATE_STEP = ESTIMATE_TUNING.stepMinutes
export const ESTIMATE_MIN = ESTIMATE_TUNING.floorMinutes

export interface EstimateStepperProps {
  /** Minutes. Always clamped into [minMinutes, maxMinutes] before it is shown. */
  value: number
  onChange: (next: number) => void
  /** Accessible name for the group wrapper. */
  label: string
  /**
   * Trailing clause on each button's accessible name, e.g. `for Two Sum`. The
   * tray renders one stepper per card, so its names have to disambiguate;
   * the custom-card form has exactly one and passes nothing.
   */
  subject?: string
  minMinutes?: number
  maxMinutes?: number
  /** Overrides the value colour — the tray paints overridden estimates amber. */
  valueColor?: string
}

/**
 * The one ±5-minute estimate control. Both the staging tray and the
 * custom-card form render it, so the step size, the bound behaviour, the
 * accessible names, and the value formatting cannot drift apart — an earlier
 * copy-paste had already leaked a hardcoded "5 minutes" into one of the aria
 * labels and a raw `${minutes}m` into one of the value spans.
 *
 * Bounds are `aria-disabled`, not `disabled`: a button that disables *itself*
 * at the bound it walks toward gets blurred by the browser on the click that
 * reaches it, dropping focus to <body> and ejecting a keyboard user from the
 * form mid-edit. Staying focusable and clamping to a no-op keeps the caret
 * where the coach put it.
 */
export function EstimateStepper(props: EstimateStepperProps): ReactNode {
  const {
    value,
    onChange,
    label,
    subject,
    minMinutes = ESTIMATE_MIN,
    maxMinutes = ESTIMATE_TUNING.ceilingMinutes,
    valueColor,
  } = props
  const suffix = subject ? ` ${subject}` : ''
  const atMin = value <= minMinutes
  const atMax = value >= maxMinutes
  const step = (delta: number): void => {
    const next = Math.min(maxMinutes, Math.max(minMinutes, value + delta))
    if (next !== value) onChange(next)
  }

  return (
    <span
      role="group"
      aria-label={label}
      className="flex shrink-0 items-center gap-1"
    >
      <button
        type="button"
        aria-label={`Decrease estimate${suffix} by ${ESTIMATE_STEP} minutes`}
        aria-disabled={atMin}
        onClick={() => step(-ESTIMATE_STEP)}
        className="cursor-pointer rounded border border-(--cd-border-2) px-1 text-[10px] leading-4 text-(--cd-text-muted) aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
      >
        −
      </button>
      {/* Announced on change: the buttons' names never vary, so without a live
          region a screen-reader user pressing "+" five times hears nothing at
          all and has to browse away from the control to learn the value. */}
      <span
        aria-live="polite"
        className="min-w-[2.5rem] whitespace-nowrap text-center text-[10px] font-semibold"
        style={{ color: valueColor ?? 'var(--cd-text)' }}
      >
        {formatMinutesDuration(value)}
      </span>
      <button
        type="button"
        aria-label={`Increase estimate${suffix} by ${ESTIMATE_STEP} minutes`}
        aria-disabled={atMax}
        onClick={() => step(ESTIMATE_STEP)}
        className="cursor-pointer rounded border border-(--cd-border-2) px-1 text-[10px] leading-4 text-(--cd-text-muted) aria-disabled:cursor-not-allowed aria-disabled:opacity-40"
      >
        +
      </button>
    </span>
  )
}
